import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ToastContainer } from 'react-toastify';
import store, { persistor } from './redux/store';
import RoutesWrapper from './Router';
import { SocketProvider } from './Pages/Chats/SocketContext';
import 'react-toastify/dist/ReactToastify.css';
import './styles/index.scss';
import { THEME_OPTIONS } from './Utils/constantData';
import { useNetworkDisconnection } from './hooks/useNetworkDisconnection';
import NoInternet from './Components/NoInternet';
import AppUpdateNotifier from './Components/AppUpdateNotifier';
import { tranlate } from './LocalizationJson/i18';

// import dotenv from 'dotenv';
// dotenv.config();

/**
 * Renders the main application component with theming, global state, and routing.
 * @example
 * App()
 * JSX.Element
 * @param {void} none - No arguments required.
 * @returns {JSX.Element} The fully configured application component, ready for rendering.
 * @description
 *   - Adds or removes a class on the body element based on the presence of a backdrop.
 *   - Disables console output in non-development environments.
 *   - Observes DOM mutations to reactively update styling based on backdrop presence.
 *   - Provides a scroll-to-top effect on route change.
 */
export const App = () => {
  const theme = createTheme(THEME_OPTIONS);
  const isOnline = useNetworkDisconnection();

  useEffect(() => {
    // Function to check for the backdrop
    const checkBackdrop = () => {
      const backdrop = document.querySelector('.MuiBackdrop-root');
      if (backdrop) {
        document.body.classList.add('backdrop-open');
      } else {
        document.body.classList.remove('backdrop-open');
      }
    };

    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver(() => checkBackdrop());

    // Start observing changes in the entire body
    observer.observe(document.body, { childList: true, subtree: true });

    // Run once on mount to check the initial state
    checkBackdrop();

    // Cleanup observer on unmount
    return () => observer.disconnect();
  }, []);

  // if (process.env.mode !== 'dev') {
  //   console.log = () => {};
  //   console.error = () => {};
  //   console.debug = () => {};
  //   console.warn = () => {};
  // }

  if (!isOnline)
    return (
      <NoInternet
        heading="No Internet Connection"
        title="Please check your connection and try again."
      />
    );

  /**
   * Automatically scrolls the window to the top when the location changes.
   * @example
   * scrollHandler()
   * null
   * @param {Object} location - Current location object containing pathname.
   * @returns {null} Always returns null since it only performs side effects.
   * @description
   *   - Utilizes useEffect to trigger scrolling behavior upon pathname changes.
   *   - Ensures smooth scroll experience using the 'behavior: smooth' option.
   *   - Blurs the active element to avoid undesirable scrolling behavior caused by focused inputs.
   */
  const ScrollToTop = () => {
    const { pathname } = useLocation();

    useEffect(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      // Blur active element to prevent scroll jumps from inputs
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, [pathname]);

    return null;
  };
  tranlate;
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <SocketProvider>
          <PersistGate loading={null} persistor={persistor}>
            <BrowserRouter>
              <ScrollToTop />
              <RoutesWrapper />
              {/* <NotificationService /> */}
            </BrowserRouter>
          </PersistGate>
        </SocketProvider>
        {/* <RouterProvider
        router={router}  
        fallbackElement={<p>Initial Load...</p>}
      /> */}
        <ToastContainer hideProgressBar />
        <AppUpdateNotifier />
      </Provider>
      <div id="recaptcha-container" style={{ display: 'none' }}></div>
    </ThemeProvider>
  );
};
